import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Static literals referenced by contentSchema (required for static extraction).
// Per-page defaults are supplied via props and applied through the dynamic
// definitions built in the component below.
const leftCtaDef: SlotGroupDefinition = {
  label: 'Left Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Your Space' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const rightCtaDef: SlotGroupDefinition = {
  label: 'Right Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function makeCtaDef(label: string, defaultLabel: string, defaultUrl: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      url: { label: 'URL', type: 'url', defaultValue: defaultUrl },
      label: { label: 'Link Text', type: 'text', defaultValue: defaultLabel },
      _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    },
  };
}

interface ExploreCTAProps {
  leftLabel?: string;
  leftUrl?: string;
  rightLabel?: string;
  rightUrl?: string;
}

export function ExploreCTA({
  leftLabel = 'Explore Your Space',
  leftUrl = '/floor-plans',
  rightLabel = 'Explore Amenities',
  rightUrl = '/amenities',
}: ExploreCTAProps) {
  const leftDef = useMemo(
    () => makeCtaDef('Left Link', leftLabel, leftUrl),
    [leftLabel, leftUrl],
  );
  const rightDef = useMemo(
    () => makeCtaDef('Right Link', rightLabel, rightUrl),
    [rightLabel, rightUrl],
  );

  const left = useSlotGroup('explorecta.left', leftDef);
  const right = useSlotGroup('explorecta.right', rightDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-surface)] border-t border-t-[var(--vibe-color-border,#e5e5e5)] border-b-4 border-b-[var(--vibe-color-primary)]"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--vibe-color-border,#e5e5e5)]">
        <EditableSlotGroup groupPath="explorecta.left" definition={leftDef}>
          <a
            href={left.url || leftUrl}
            target={left._target || '_self'}
            className="group flex items-center justify-center gap-3 whitespace-nowrap bg-white px-4 sm:px-6 lg:px-8 py-8 lg:py-9 font-heading text-[13px] sm:text-sm lg:text-base font-semibold uppercase tracking-[0.12em] sm:tracking-[0.15em] lg:tracking-[0.2em] text-[var(--vibe-color-text)] transition-colors duration-300 hover:text-[var(--vibe-color-primary)]"
          >
            {left.label || leftLabel}
            <span className="transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden>
              →
            </span>
          </a>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="explorecta.right" definition={rightDef}>
          <a
            href={right.url || rightUrl}
            target={right._target || '_self'}
            className="group flex items-center justify-center gap-3 whitespace-nowrap bg-white px-4 sm:px-6 lg:px-8 py-8 lg:py-9 font-heading text-[13px] sm:text-sm lg:text-base font-semibold uppercase tracking-[0.12em] sm:tracking-[0.15em] lg:tracking-[0.2em] text-[var(--vibe-color-text)] transition-colors duration-300 hover:text-[var(--vibe-color-primary)]"
          >
            {right.label || rightLabel}
            <span className="transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden>
              →
            </span>
          </a>
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ExploreCTA',
  displayName: 'Explore CTA Bar',
  tokens: {},
  slotGroups: {
    'explorecta.left': leftCtaDef,
    'explorecta.right': rightCtaDef,
  },
};

(ExploreCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ExploreCTA;
